import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';

import { cn } from '@/lib/cn';

export interface TagOption {
  id: number;
  label: string;
}

interface TagsComboboxProps {
  selected: number[];
  options: TagOption[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
}

/**
 * Multi-select tipo tags con búsqueda. Muestra los seleccionados como chips
 * con su X para quitarlos, y un input para buscar/agregar.
 */
export function TagsCombobox({
  selected,
  options,
  onChange,
  placeholder = 'Buscar…',
  emptyMessage = 'Sin resultados',
  className,
}: TagsComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const available = options
      .filter((o) => !selectedSet.has(o.id))
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    if (!q) return available.slice(0, 200);
    return available.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 200);
  }, [options, query, selectedSet]);

  const selectedTags = useMemo(
    () =>
      options
        .filter((o) => selectedSet.has(o.id))
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })),
    [options, selectedSet],
  );

  const add = (id: number) => {
    if (selectedSet.has(id)) return;
    onChange([...selected, id]);
    setQuery('');
  };

  const remove = (id: number) => {
    onChange(selected.filter((x) => x !== id));
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className="input flex flex-wrap gap-1.5 cursor-text min-h-[38px] py-1.5"
        onClick={() => setOpen(true)}
      >
        {selectedTags.map((t) => (
          <span
            key={t.id}
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-notion-100 text-notion-800"
          >
            {t.label}
            <button
              type="button"
              className="text-notion-500 hover:text-notion-800"
              onClick={(e) => {
                e.stopPropagation();
                remove(t.id);
              }}
              aria-label={`Quitar ${t.label}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
        <input
          type="text"
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-notion-200 bg-white shadow-sm">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-notion-500">{emptyMessage}</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-notion-100 inline-flex items-center gap-2"
                onClick={() => add(o.id)}
              >
                <Plus size={12} className="text-notion-400" />
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
